import { cloneObject } from '../utilities';
import { Fields } from '../domain/content/entities/valueobjects/Fields';
import { BorderTypes, FontFormat, FontFormatPositions, HTMLSpacing, HTMLUnit, LineHeight, Shadow } from '../domain/content/entities/valueobjects/Style';
/**
 * WARNING - DO NOT TOUCH
 *
 * ALL THE FUNCTIONS ARE USED MULTIPLE TIMES IN CONTENT VALIDATOR !
 *
 * DO NOT TOUCH WITHOUT TALKING TO EVERYBODY
 *
 */
const getFormatObject = (size = 12, weight: string | number = '400', position: FontFormatPositions = 'left', color = '#000000'): FontFormat => {
  return {
    enabled: true,
    position: position,
    size: size,
    weight: String(weight),
    color: color,
    fontFamily: "'Roboto', sans-serif",
    letterSpacing: 0,
  };
};

const getFormatShadowObject = (x = 0, y = 0, blur = 0, spread = 0, color = 'transparent') => {
  return {
    x: x,
    y: y,
    blur: blur,
    spread: spread,
    color: color,
  };
};

const getFormatLineHeightObject = (value = 'initial', unit: HTMLUnit = 'em') => {
  return {
    value: value,
    unit: unit,
  };
};

const getFormatMarginPaddingObject = (
  top = 0,
  topUnit: HTMLUnit = 'px',
  right = 0,
  rightUnit: HTMLUnit = 'px',
  bottom = 0,
  bottomUnit: HTMLUnit = 'px',
  left = 0,
  leftUnit: HTMLUnit = 'px',
) => {
  return {
    top: top,
    right: right,
    topUnit: topUnit,
    bottom: bottom,
    left: left,
    rightUnit: rightUnit,
    bottomUnit: bottomUnit,
    leftUnit: leftUnit,
  };
};
/**
 * Gets the Extended Format Object which is our base reference for
 * all kinds of formatting when displaying strings in a visual
 * @param size
 * @param weight
 * @param position
 * @param color
 * @param shadow
 * @param lineheight
 * @param padding
 * @param margin
 * @returns
 */
const getExtendedFormatObject = (
  size?: number,
  weight?: string | number,
  position?: FontFormatPositions,
  color?: string,
  shadow?: Shadow,
  lineheight?: LineHeight,
  padding?: HTMLSpacing,
  margin?: HTMLSpacing,
): FontFormat => {
  const format = getFormatObject(size, weight, position, color);
  format['shadow'] = shadow ? shadow : getFormatShadowObject();
  format['lineheight'] = lineheight ? lineheight : getFormatLineHeightObject();
  format['margin'] = margin ? margin : getFormatMarginPaddingObject();
  format['padding'] = padding ? padding : getFormatMarginPaddingObject();
  return format;
};

const convertFormatToExtendedFormat = (format, extendedFormat) => {
  format.enabled = format.hasOwnProperty('enabled') ? format.enabled : true;
  format.position = format.hasOwnProperty('position') ? format.position : 'left';
  format.size = format.hasOwnProperty('size') ? format.size : 12;
  format.weight = format.hasOwnProperty('weight') ? String(format.weight) : '300';
  format.color = format.hasOwnProperty('color') ? format.color : '#000000';
  format.fontFamily = format.hasOwnProperty('fontFamily') ? format.fontFamily : "'Work Sans', sans-serif";
  format.letterSpacing = format.hasOwnProperty('letterSpacing') ? format.letterSpacing : 0;

  format.shadow = format.shadow || cloneObject(extendedFormat.shadow);
  format.margin = format.margin || cloneObject(extendedFormat.margin);
  format.padding = format.padding || cloneObject(extendedFormat.padding);
  format.lineheight = format.lineheight || cloneObject(extendedFormat.lineheight);
  return format;
};

const getBorderObject = (color = '#000', type:BorderTypes = 'none', size = 1) => {
  return {
    color: color,
    size: {
      top: size,
      right: size,
      bottom: size,
      left: size,
    },
    type: type,
  };
};

/**
 * generates a dropdown object for style
 * @param font
 * @param padding
 * @param background
 * @returns
 */
const getDropdownObject = (font, padding, background) => {
  return {
    search: {
      enabled: true,
      placeholder: 'Suche...',
    },
    selectAllButton: {
      label: 'Alle auswählen',
    },
    items: {
      ordering: {
        enabled: true,
        desc: false,
      },
      font: {
        ...(font || getExtendedFormatObject()),
        margin: getFormatMarginPaddingObject(0, 'px', 0, 'px', 0,'px', 0, 'px'),
        padding: padding || getFormatMarginPaddingObject(5, 'px', 10, 'px', 5, 'px', 10, 'px'),
      },
      noItemsFound: 'No items found',
      inactive: {
        opacity: 0.4,
      },
      active: {
        color: font ? font.color : 'currentColor',
        background: {
          backgroundColor: 'rgba(0,0,0,0)',
        },
      },
      mouseOver: {
        color: font ? font.color : 'currentColor',
        background: {
          backgroundColor: 'rgba(0,0,0,0.05)',
        },
      },
    },
    container: {
      background: { ...(background || { backgroundColor: '#fff' }) },
      boxShadow: getFormatShadowObject(0, 10, 16, 0, 'rgba(0,0,0,0.1)'),
      borderRadius: 0,
      padding: getFormatMarginPaddingObject(0, 'px', 0, 'px', 0, 'px', 0, 'px'),
      applyStyleToSourceButton: false,
    },
  };
};

const convertLineHeight = (lineHeight: LineHeight | string): string => {
  if (!lineHeight) return 'initial';
  if (typeof lineHeight === 'string') {
    return lineHeight;
  }

  // if (lineHeight.value === 'initial') return 'initial';

  return `${lineHeight.value}`;
  // return `${lineHeight.value}${lineHeight.unit}`;
};

const convertShadow = (shadow: Shadow): string => {
  if (!shadow) return '';
  return `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color}`;
};

const convertFontFormatToQuillFormat = (fontFormat: FontFormat) => {
  const quillFormat: any = {};
  if (fontFormat.color) quillFormat.color = fontFormat.color;
  if (fontFormat.fontFamily) quillFormat.font = fontFormat.fontFamily;
  if (fontFormat.size) quillFormat.size = `${fontFormat.size}px`;
  if (fontFormat.weight) quillFormat.weight = fontFormat.weight.toString().replace('i', '');
  if (fontFormat.weight?.includes('i')) quillFormat.fontStyle = 'italic';
  if (fontFormat.lineheight) quillFormat.lineHeight = convertLineHeight(fontFormat.lineheight);
  if (fontFormat.shadow) quillFormat.textShadow = convertShadow(fontFormat.shadow);
  if (fontFormat.contrastColorLight) quillFormat.contrastColorLight = fontFormat.contrastColorLight;
  if (fontFormat.contrastColorDark) quillFormat.contrastColorDark = fontFormat.contrastColorDark;
  return quillFormat;
};

const updateInlineValueStyle = (quillStr: string, fontStyle: FontFormat, fields: Fields) => {
  if (!Array.isArray(fontStyle.presets)) return quillStr; // nothing to do here
  if (quillStr?.substring(0, 1) !== '{') return quillStr;

  try {
    const quillObj = JSON.parse(quillStr);
    for (const op of quillObj.ops) {
      if (op.insert.hasOwnProperty('inlineValue')) {
        const { fieldName } = op.insert.inlineValue;
        const { fieldType, dataType } = fields[fieldName];

        const preset = fontStyle.presets.find((obj) => obj.type === dataType);
        if (preset) {
          op.attributes = convertFontFormatToQuillFormat(preset);
        }
      }
    }

    return JSON.stringify(quillObj);
  } catch (error) {
    console.log('[updateInlineValueStyle] error:', error);
    return quillStr;
  }
};

const removeOpAttributes = (quillStr: string) => {
  if (quillStr?.substring(0, 1) !== '{') return quillStr;

  try {
    const quillObj = JSON.parse(quillStr);
    for (const op of quillObj.ops) {
      if (op.attributes) {
        delete op.attributes;
      }
    }

    return JSON.stringify(quillObj);
  } catch (error) {
    console.log('[removeOpAttributes] error:', error);
    return quillStr;
  }
};

export {
  getFormatObject,
  getExtendedFormatObject,
  getFormatShadowObject,
  getFormatLineHeightObject,
  getFormatMarginPaddingObject,
  convertFormatToExtendedFormat,
  getBorderObject,
  getDropdownObject,
  convertLineHeight,
  convertShadow,
  convertFontFormatToQuillFormat,
  updateInlineValueStyle,
  removeOpAttributes,
};
